/**
 * Centralized error handling service
 */
import { AxiosError } from 'axios'
import { AppError, ErrorCategory, ErrorSeverity, createNetworkError, createApiError, isRetryableError } from '@/utils/errorUtils'
import { alertDialogService } from '@/services/alertDialogService'
import i18n from '@/i18n'

const { t } = i18n.global

// Flag to track if we're currently offline
let isOffline = typeof navigator !== 'undefined' ? !navigator.onLine : false

// Store the last few errors for debugging
const errorHistory: AppError[] = []
const MAX_ERROR_HISTORY = 10

// Keep track of global error handlers
type ErrorHandler = (error: AppError) => boolean | void
const errorHandlers: ErrorHandler[] = []

/**
 * Handle an error through the centralized error handling service
 * @param error The error to handle
 * @param context Additional context for the error
 * @returns The processed AppError
 */
function handleError(error: unknown, context?: Record<string, any>): AppError {
  // Convert to AppError format
  const appError = processError(error, context)

  // Add to error history
  addToErrorHistory(appError)

  // Log the error
  logError(appError)

  // Run through registered error handlers
  let handled = false
  for (const handler of errorHandlers) {
    const result = handler(appError)
    if (result === true) {
      handled = true
      break
    }
  }

  // If not handled by a custom handler, use default handling
  if (!handled) {
    defaultErrorHandler(appError)
  }

  return appError
}

/**
 * Process an error into the standard AppError format
 */
function processError(error: unknown, context?: Record<string, any>): AppError {
  // Already an AppError
  if (error && typeof error === 'object' && 'category' in error && 'severity' in error) {
    return error as AppError
  }

  // Axios error
  if (error && typeof error === 'object' && 'isAxiosError' in error && error.isAxiosError) {
    return createApiError(error as AxiosError, context)
  }

  // Network error
  if (error instanceof Error && (
    error.message.includes('Network Error') ||
    error.message.includes('network') ||
    error.message.includes('timeout') ||
    error.message.includes('connection')
  )) {
    return createNetworkError(error, context)
  }

  // Generic error
  if (error instanceof Error) {
    return {
      message: error.message || t('common.error'),
      category: ErrorCategory.UNKNOWN,
      severity: ErrorSeverity.ERROR,
      originalError: error,
      timestamp: Date.now(),
      context
    }
  }

  // Unknown error
  return {
    message: error ? String(error) : t('common.error'),
    category: ErrorCategory.UNKNOWN,
    severity: ErrorSeverity.ERROR,
    originalError: error,
    timestamp: Date.now(),
    context
  }
}

/**
 * Add an error to the error history
 */
function addToErrorHistory(error: AppError): void {
  errorHistory.unshift(error)

  // Limit the size of the error history
  if (errorHistory.length > MAX_ERROR_HISTORY) {
    errorHistory.pop()
  }
}

/**
 * Log an error to the console
 */
function logError(error: AppError): void {
  // Log to console with appropriate level
  switch (error.severity) {
    case ErrorSeverity.INFO:
      console.info('[ERROR SERVICE]', error.message, error)
      break
    case ErrorSeverity.WARNING:
      console.warn('[ERROR SERVICE]', error.message, error)
      break
    case ErrorSeverity.ERROR:
    case ErrorSeverity.CRITICAL:
    default:
      console.error('[ERROR SERVICE]', error.message, error)
      break
  }

  // In production, you might want to send errors to a monitoring service
  if (process.env.NODE_ENV === 'production') {
    // Example: sendToErrorMonitoring(error)
  }
}

/**
 * Default error handler that displays errors to the user
 */
function defaultErrorHandler(error: AppError): void {
  // Check if we're offline
  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    isOffline = true

    // Show an offline alert dialog
    alertDialogService.networkError(
      t('errors.networkErrorTitle'),
      t('errors.offline'),
      {
        showRetryButton: true,
        onRetry: () => {
          // Check if we're back online
          if (navigator.onLine) {
            isOffline = false
            // You could trigger a refresh or retry failed requests here
          } else {
            // Still offline, show the alert again
            defaultErrorHandler(error)
          }
        }
      }
    )
    return
  }

  // Use alert dialog for all errors
  alertDialogService.showAppError(error)
}

/**
 * Register a global error handler
 * @param handler The error handler function
 * @returns A function to unregister the handler
 */
function registerErrorHandler(handler: ErrorHandler): () => void {
  errorHandlers.push(handler)

  // Return a function to unregister the handler
  return () => {
    const index = errorHandlers.indexOf(handler)
    if (index !== -1) {
      errorHandlers.splice(index, 1)
    }
  }
}

/**
 * Get the error history
 */
function getErrorHistory(): AppError[] {
  return [...errorHistory]
}

/**
 * Clear the error history
 */
function clearErrorHistory(): void {
  errorHistory.length = 0
}

/**
 * Set up online/offline event listeners
 */
function setupNetworkListeners(): void {
  if (typeof window === 'undefined') return

  // Listen for online events
  window.addEventListener('online', () => {
    isOffline = false
    console.log('Connection restored')

    // Show a small info alert dialog
    alertDialogService.info(
      t('errors.connectionRestoredTitle'),
      t('errors.connectionRestored'),
      {
        autoClose: true, // Auto close after a few seconds
        duration: 3000
      }
    )

    // You could trigger a refresh or retry failed requests here
  })

  // Listen for offline events
  window.addEventListener('offline', () => {
    isOffline = true
    console.log('Connection lost')

    // Show an offline alert dialog
    alertDialogService.networkError(
      t('errors.networkErrorTitle'),
      t('errors.offline'),
      {
        showRetryButton: true,
        onRetry: () => {
          // Check if we're back online
          if (navigator.onLine) {
            isOffline = false
            // You could trigger a refresh or retry failed requests here
          }
        }
      }
    )
  })
}

// Set up network listeners
setupNetworkListeners()

export const errorService = {
  handleError,
  registerErrorHandler,
  getErrorHistory,
  clearErrorHistory,
  isOffline: () => isOffline
}
